import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Clock, Globe, Search, ArrowRight, MapPin, Calendar, Monitor, Users, DollarSign } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

// Placeholder image for courses without images
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=400&fit=crop';

export const CoursesPage = () => {
  const { t, language, direction } = useLanguage();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('all');
  const [deliveryMode, setDeliveryMode] = useState('all');
  const [courseLanguage, setCourseLanguage] = useState('all');
  const [location, setLocation] = useState('all');
  const [yearMonth, setYearMonth] = useState('all');

  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchAvailableMonths();
    fetchCourses();
  }, [categoryId, deliveryMode, courseLanguage, location, yearMonth]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/courses/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${API}/courses/available-months`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMonths(data.months || []);
      }
    } catch (error) {
      console.error('Error fetching available months:', error);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      let url = `${API}/courses?`;
      if (categoryId && categoryId !== 'all') url += `&category_id=${categoryId}`;
      if (deliveryMode && deliveryMode !== 'all') url += `&delivery_mode=${deliveryMode}`;
      if (courseLanguage && courseLanguage !== 'all') url += `&language=${courseLanguage}`;
      if (location && location !== 'all') url += `&location=${encodeURIComponent(location)}`;
      if (yearMonth && yearMonth !== 'all') url += `&year_month=${yearMonth}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCourses();
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get course image
  const getCourseImage = (course) => {
    return course.course_image || course.image_url || PLACEHOLDER_IMAGE;
  };

  // Format month for display (e.g., "2026-05" -> "May 2026" or "مايو ٢٠٢٦")
  const formatYearMonth = (yearMonth) => {
    if (!yearMonth) return '';
    try {
      const [year, month] = yearMonth.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return yearMonth;
    }
  };

  // Get delivery mode icon and label
  const getDeliveryModeInfo = (mode) => {
    switch (mode) {
      case 'online':
        return { icon: Monitor, label: t('training.online') };
      case 'onsite':
        return { icon: Users, label: t('training.onsite') };
      case 'hybrid':
        return { icon: Globe, label: t('training.hybrid') };
      default:
        return { icon: BookOpen, label: mode };
    }
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الدورات التدريبية' : 'Training Courses' }
  ]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="courses-page">
      <SEO
        title="Professional Training Courses & Programs"
        titleAr="الدورات التدريبية والبرامج المهنية"
        description="Explore professional training courses in Europe, Middle East, GCC, and Africa. Online, on-site, and hybrid delivery. Leadership, management, HR, finance, IT, and more. Enroll now with WeDo HR Services."
        descriptionAr="اكتشف الدورات التدريبية المهنية في أوروبا والشرق الأوسط ودول الخليج وأفريقيا. تدريب عبر الإنترنت وحضوري ومختلط. القيادة والإدارة والموارد البشرية والمالية وتكنولوجيا المعلومات والمزيد. سجل الآن مع ويدو."
        keywords="training courses, professional development, corporate training, دورات تدريبية, تدريب مهني, online training, leadership training, management courses, HR training, Europe training, Middle East training, GCC courses"
        canonicalUrl="/courses"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] mb-4">
            {t('nav.courses')}
          </h1>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            {t('services.training.description')}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-[#E5E7EB] py-6 sticky top-16 md:top-20 z-40">
        <div className="container-custom">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B7280]" />
              <Input
                type="text"
                placeholder={language === 'ar' ? 'البحث عن تدريب...' : 'Search for a training...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-10 input-focus"
                data-testid="course-search-input"
              />
            </div>
            
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.category_id}>
                    {language === 'ar' ? cat.name_ar : cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={deliveryMode} onValueChange={setDeliveryMode}>
              <SelectTrigger className="w-full md:w-40" data-testid="delivery-filter">
                <SelectValue placeholder={t('training.mode')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="online">{t('training.online')}</SelectItem>
                <SelectItem value="onsite">{t('training.onsite')}</SelectItem>
                <SelectItem value="hybrid">{t('training.hybrid')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={courseLanguage} onValueChange={setCourseLanguage}>
              <SelectTrigger className="w-full md:w-40" data-testid="language-filter">
                <SelectValue placeholder={t('training.language')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                <SelectItem value="english">{t('training.english')}</SelectItem>
                <SelectItem value="arabic">{t('training.arabic')}</SelectItem>
                <SelectItem value="bilingual">{t('training.bilingual')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger className="w-full md:w-40" data-testid="location-filter">
                <SelectValue placeholder={language === 'ar' ? 'الموقع' : 'Location'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'ar' ? 'جميع المواقع' : 'All Locations'}</SelectItem>
                {locations.map((loc, index) => (
                  <SelectItem key={index} value={loc.location}>
                    {language === 'ar' ? loc.location_ar : loc.location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={yearMonth} onValueChange={setYearMonth}>
              <SelectTrigger className="w-full md:w-44" data-testid="month-filter">
                <SelectValue placeholder={language === 'ar' ? 'الشهر' : 'Month'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'ar' ? 'جميع الأشهر' : 'All Months'}</SelectItem>
                {availableMonths.map((m) => (
                  <SelectItem key={m.year_month} value={m.year_month}>
                    {formatYearMonth(m.year_month)} ({m.session_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button type="submit" className="btn-primary" data-testid="search-courses-btn">
              <Search className="h-4 w-4 me-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-12">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD] mx-auto"></div>
              <p className="mt-4 text-[#6B7280]">{t('common.loading')}</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-[#E5E7EB] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#111827] mb-2">No courses found</h3>
              <p className="text-[#6B7280] mb-6">Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, index) => {
                const deliveryInfo = getDeliveryModeInfo(course.delivery_mode);
                const DeliveryIcon = deliveryInfo.icon;
                const startDate = formatDate(course.start_date);
                const endDate = formatDate(course.end_date);
                
                return (
                  <Card key={course.course_id} className="card-hover overflow-hidden group" data-testid={`course-card-${index}`}>
                    {/* Course Image */}
                    <div className="aspect-video bg-gradient-to-br from-[#33AADD]/20 to-[#33AADD]/5 relative overflow-hidden">
                      <img 
                        src={getCourseImage(course)} 
                        alt={language === 'ar' ? course.title_ar : course.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                      />
                      {course.is_featured && (
                        <Badge className="absolute top-3 end-3 bg-[#33AADD]">Featured</Badge>
                      )}
                      {/* Delivery Mode Overlay */}
                      <div className="absolute bottom-3 start-3">
                        <Badge className="bg-white/90 text-[#111827] backdrop-blur-sm flex items-center gap-1">
                          <DeliveryIcon className="h-3 w-3" />
                          {deliveryInfo.label}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent className="p-5">
                      {/* Course Title */}
                      <h3 className="text-lg font-semibold text-[#111827] mb-3 line-clamp-2">
                        {language === 'ar' ? course.title_ar : course.title}
                      </h3>
                      
                      {/* Key Info Grid */}
                      <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                        {/* Location */}
                        {course.location && (
                          <div className="flex items-center gap-1.5 text-[#6B7280]">
                            <MapPin className="h-3.5 w-3.5 text-[#33AADD]" />
                            <span className="truncate">
                              {language === 'ar' ? course.location_ar || course.location : course.location}
                            </span>
                          </div>
                        )}
                        
                        {/* Duration */}
                        <div className="flex items-center gap-1.5 text-[#6B7280]">
                          <Clock className="h-3.5 w-3.5 text-[#33AADD]" />
                          <span>{course.duration_days} {t('training.days')}</span>
                        </div>
                        
                        {/* Language */}
                        <div className="flex items-center gap-1.5 text-[#6B7280]">
                          <Globe className="h-3.5 w-3.5 text-[#33AADD]" />
                          <span>
                            {course.language === 'english' ? t('training.english') : 
                             course.language === 'arabic' ? t('training.arabic') : t('training.bilingual')}
                          </span>
                        </div>
                        
                        {/* Date Range */}
                        {(startDate || endDate) && (
                          <div className="flex items-center gap-1.5 text-[#6B7280]">
                            <Calendar className="h-3.5 w-3.5 text-[#33AADD]" />
                            <span className="truncate">
                              {startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Price & CTA */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
                        {course.price ? (
                          <div className="flex items-center gap-1">
                            <span className="font-semibold text-[#33AADD]">EUR</span>
                            <span className="font-semibold text-[#111827]">
                              {course.price.toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-[#6B7280]">{t('training.requestQuote')}</span>
                        )}
                        <Link to={`/courses/${course.course_id}`}>
                          <Button size="sm" className="btn-primary" data-testid={`view-course-${index}`}>
                            {t('common.learnMore')}
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Custom Training CTA */}
          <div className="mt-16 p-8 bg-[#33AADD] rounded-2xl text-center text-white">
            <h3 className="text-2xl font-bold mb-2">
              {t('training.customTraining')}
            </h3>
            <p className="text-white/90 mb-6 max-w-lg mx-auto">
              {t('training.customTrainingDesc')}
            </p>
            <Link to="/customized-training-request">
              <Button variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90" data-testid="custom-training-cta-btn">
                {t('training.requestCustom')}
                <ArrowRight className={`ms-2 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
